import { useEffect, useMemo, useState } from "react";
import { buildersApi, ApiError } from "@/api";
import type { BuilderListRow, JobBuilderRole, JobBuilderLinkResponse } from "@/types";
import { JOB_BUILDER_ROLES } from "@/types";
import { Button, TextField, SelectField } from "@/components/ui";
import { jobBuilderRoleLabel } from "../builders/BuilderDetail";
import { FormDrawer } from "./FormDrawer";
import "../builders/builders.css";

type Mode = "pick" | "create";

/** Attach a builder firm to the job — pick from the directory (already-attached
 *  firms hidden) or create a new firm inline and attach it in one go. The
 *  firm's app logins see the chat from now on unless "Show the full history"
 *  is ticked (Moxo-era builders); the client sees a notice either way (D7). */
export function AddBuilderDrawer({
  jobId,
  attachedIds,
  onClose,
  onSaved,
}: {
  jobId: number;
  attachedIds: number[];
  onClose: () => void;
  onSaved: (res: JobBuilderLinkResponse) => void;
}) {
  const [mode, setMode] = useState<Mode>("pick");
  const [rows, setRows] = useState<BuilderListRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<BuilderListRow | null>(null);
  const [role, setRole] = useState<JobBuilderRole>("appointed");
  const [fullHistory, setFullHistory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Inline create.
  const [company, setCompany] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [trade, setTrade] = useState("");

  useEffect(() => {
    let cancelled = false;
    buildersApi
      .listBuilders()
      .then((res) => {
        if (!cancelled) setRows(res.items);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : "Could not load builders.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const candidates = useMemo(() => {
    if (!rows) return [];
    const needle = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (r.is_archived || attachedIds.includes(r.id)) return false;
      if (!needle) return true;
      return `${r.company_name} ${r.contact_name} ${r.email} ${r.trade}`.toLowerCase().includes(needle);
    });
  }, [rows, query, attachedIds]);

  async function attach(builderId: number) {
    const res = await buildersApi.attachToJob(jobId, {
      builder_id: builderId,
      role,
      show_full_history: fullHistory,
    });
    onSaved(res);
  }

  async function submitPick() {
    if (busy || !picked) return;
    setBusy(true);
    setError(undefined);
    try {
      await attach(picked.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not add the builder.");
      setBusy(false);
    }
  }

  async function submitCreate() {
    if (busy) return;
    if (!company.trim()) {
      setError("Enter the company name.");
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const created = await buildersApi.createBuilder({
        company_name: company.trim(),
        contact_first_name: first.trim() || undefined,
        contact_last_name: last.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        trade: trade.trim() || undefined,
      });
      await attach(created.builder.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the builder.");
      setBusy(false);
    }
  }

  const visibilityFields = (
    <section className="job-drawer__section">
      <h3>On this job</h3>
      <SelectField label="Role" value={role} onChange={(e) => setRole(e.target.value as JobBuilderRole)}>
        {JOB_BUILDER_ROLES.map((r) => (
          <option key={r} value={r}>{jobBuilderRoleLabel(r)}</option>
        ))}
      </SelectField>
      <label className="lead-drawer__check">
        <input type="checkbox" checked={fullHistory} onChange={(e) => setFullHistory(e.target.checked)} />
        Show them the full chat history
      </label>
      <p className="job-drawer__muted">
        {fullHistory
          ? "Their app logins will see every message on this job, including earlier ones."
          : "Their app logins will see messages from now on; earlier messages stay private to you and the client."}{" "}
        The client sees a note in the chat that the builder has been added.
      </p>
    </section>
  );

  return (
    <FormDrawer
      title="Add builder to job"
      busy={busy}
      onClose={onClose}
      closeOnOverlayClick={false}
      footer={
        mode === "pick" ? (
          <>
            <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={submitPick} loading={busy} disabled={!picked || busy}>Add to job</Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => setMode("pick")} disabled={busy}>Back</Button>
            <Button variant="primary" onClick={submitCreate} loading={busy}>Create and add</Button>
          </>
        )
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      {mode === "pick" ? (
        <>
          <section className="job-drawer__section">
            <h3>Builder</h3>
            <TextField label="Find a builder" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Firm, contact, trade…" />
            {loadError ? <p className="leads__error" role="alert">{loadError}</p> : null}
            {rows === null ? (
              <p className="job-drawer__muted">Loading builders…</p>
            ) : (
              <ul className="builder-pick" role="listbox" aria-label="Builders">
                {candidates.length === 0 ? (
                  <li className="builder-pick__empty">
                    {rows.length === 0 ? "No builders in the directory yet." : "No builders match (already-attached and archived firms are hidden)."}
                  </li>
                ) : (
                  candidates.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        className={`builder-pick__row${picked?.id === r.id ? " is-picked" : ""}`}
                        role="option"
                        aria-selected={picked?.id === r.id}
                        onClick={() => setPicked(r)}
                      >
                        <span>
                          {r.company_name}
                          <span className="builder-pick__meta"> {[r.contact_name, r.trade].filter(Boolean).join(" · ")}</span>
                        </span>
                        <span className="builder-pick__meta">{r.job_count} job{r.job_count === 1 ? "" : "s"}</span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
            <p className="builder-pick__switch">
              <Button variant="ghost" size="sm" onClick={() => setMode("create")}>Not listed? Create a new builder</Button>
            </p>
          </section>
          {picked ? visibilityFields : null}
        </>
      ) : (
        <>
          <section className="job-drawer__section">
            <h3>New builder</h3>
            <TextField label="Company name" required value={company} onChange={(e) => setCompany(e.target.value)} />
            <div className="lead-drawer__two">
              <TextField label="Contact first name" value={first} onChange={(e) => setFirst(e.target.value)} />
              <TextField label="Contact last name" value={last} onChange={(e) => setLast(e.target.value)} />
            </div>
            <div className="lead-drawer__two">
              <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <TextField label="Trade" value={trade} onChange={(e) => setTrade(e.target.value)} />
          </section>
          {visibilityFields}
        </>
      )}
    </FormDrawer>
  );
}
